import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Sparkles, X } from "lucide-react";
import { ApiService } from "../../services/api";
import { useModal } from "../../hooks/useModal";
import DepartmentSelector from "../../components/selectors/DepartmentSelector";

/**
 * Yeni kampanya — Anketler/Toplantılar form kabuğu: üst nav + beyaz kart + bölümler + yapışkan alt şerit.
 * Burada hiçbir şey gönderilmez; üretim ve onay detay sayfasında.
 */
export default function MailMarketingNewPage() {
  const navigate = useNavigate();
  const { showToast } = useModal();

  const fieldClassName =
    "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-cyan-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-100";
  const sectionClassName = "rounded-xl border border-slate-200 bg-white p-4 sm:p-5";
  const sectionBadgeClassName = "inline-flex items-center rounded-full border border-cyan-100 bg-cyan-50 px-2.5 py-1 text-xs font-semibold text-cyan-700";

  const [form, setForm] = useState({
    name: "", type: "SERVICE" as "SERVICE" | "COMMERCIAL", mode: "BEHAVIOR" as "BEHAVIOR" | "TEMPLATE",
    channel: "EMAIL" as "EMAIL" | "SMS" | "BOTH", brief: "", template: "", subject: "",
  });
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [aud, setAud] = useState<any>({ inactiveDays: "", activeWithinDays: "", licenseExpiresInDays: "", hasOpenGains: false, limit: 500 });
  const [preview, setPreview] = useState<any>(null);
  const [previewing, setPreviewing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const audiencePayload = () => ({
    departmentIds: departments.map((d) => d.id),
    inactiveDays: aud.inactiveDays === "" ? null : Number(aud.inactiveDays),
    activeWithinDays: aud.activeWithinDays === "" ? null : Number(aud.activeWithinDays),
    licenseExpiresInDays: aud.licenseExpiresInDays === "" ? null : Number(aud.licenseExpiresInDays),
    hasOpenGains: aud.hasOpenGains ? true : null,
    limit: Number(aud.limit) || 500,
  });

  useEffect(() => { setPreview(null); }, [departments, aud, form.type, form.channel]);

  const runPreview = async () => {
    setPreviewing(true);
    try { setPreview(await ApiService.previewMailAudience({ ...audiencePayload(), type: form.type, channel: form.channel })); }
    catch (e: any) { showToast(e?.message || "Kitle hesaplanamadı", "error"); }
    finally { setPreviewing(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { showToast("Kampanya adı zorunludur", "warning"); return; }
    if (form.mode === "TEMPLATE" && !form.template.trim()) { showToast("Gönderilecek metni yaz", "warning"); return; }
    if (form.mode === "BEHAVIOR" && !form.brief.trim()) { showToast("AI yönergesi zorunludur", "warning"); return; }
    setIsLoading(true);
    try {
      const c = await ApiService.createMailCampaign({ ...form, audience: audiencePayload() });
      showToast("Kampanya oluşturuldu — şimdi örnek mesajları üret", "success");
      navigate(`/mail-marketing/${c.id}`, { replace: true });
    } catch (err: any) { showToast(err?.message || "Kampanya oluşturulamadı", "error"); }
    finally { setIsLoading(false); }
  };

  const excludedText = (ex: any) => {
    if (!ex) return null;
    const parts = [ex.revoked && `${ex.revoked} çıkmış`, ex.suppressed && `${ex.suppressed} bastırılmış`, ex.noConsent && `${ex.noConsent} rızasız`, ex.noAddress && `${ex.noAddress} adressiz`].filter(Boolean);
    return parts.length ? `Dışlanan: ${parts.join(", ")}` : null;
  };

  return (
    <div className="min-h-full bg-gray-100 flex flex-col">
      <div className="flex-1 flex flex-col">
        <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
          <div className="px-6 py-3">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2"><span className="font-semibold text-gray-900">Mail Pazarlama</span></div>
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <button onClick={() => navigate("/mail-marketing")} className="pb-3 transition-all duration-200 text-cyan-700 font-medium border-b-2 border-cyan-500">Tüm Kampanyalar</button>
              </div>
            </div>
          </div>
        </nav>

        <div className="flex-1 p-5 sm:p-6">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
              <div className="mb-5 flex items-center justify-between gap-3">
                <div>
                  <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Yeni Kampanya</h1>
                  <p className="text-sm text-gray-600 mt-1">Kitleyi seç, amacı yaz; her kişi için kendi verisine göre ayrı mesaj üretilir.</p>
                  <div className="mt-3 h-1 w-20 rounded-full bg-cyan-200" />
                </div>
                <button type="button" onClick={() => navigate("/mail-marketing")}
                  className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50">Geri Dön</button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className={sectionClassName}>
                  <div className="mb-3"><span className={sectionBadgeClassName}>1 · Kampanya</span></div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Kampanya Adı *</label>
                      <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldClassName} placeholder="Örn: Eylül — ara vermiş 8. sınıflar" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">İçerik Modu</label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[
                          { v: "BEHAVIOR", t: "Davranışa göre AI yazsın", d: "Her kişi için, o kişinin verisine (son giriş, açık eksikler, plan) bakarak sıfırdan kısa mesaj üretilir." },
                          { v: "TEMPLATE", t: "Hazır metni kişiselleştir", d: "Yazdığın metin AYNEN korunur; AI yalnız konu başlığını ve kişiye hitabı ekler. Duyuru, mektup, bilgilendirme için." },
                        ].map((o) => (
                          <label key={o.v}
                            className={`flex cursor-pointer gap-3 rounded-xl border p-3 transition ${form.mode === o.v ? "border-cyan-400 bg-cyan-50/60 ring-2 ring-cyan-100" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                            <input type="radio" name="mode" value={o.v} checked={form.mode === o.v}
                              onChange={() => setForm({ ...form, mode: o.v as any })} className="mt-1 h-4 w-4 text-cyan-600 focus:ring-cyan-500" />
                            <span><span className="block text-sm font-medium text-gray-900">{o.t}</span><span className="block text-xs text-gray-600 mt-0.5">{o.d}</span></span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Mesaj Türü</label>
                        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as any })} className={fieldClassName}>
                          <option value="SERVICE">Hizmet bildirimi — çalışma özeti / hatırlatma</option>
                          <option value="COMMERCIAL">Ticari ileti — paket / teklif (yalnız rızası olanlara)</option>
                        </select>
                        {form.type === "COMMERCIAL" && (
                          <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">6563 sayılı kanun gereği rızası (GRANTED) olmayan alıcıya mesaj üretilmez. Rıza kaydı olan kişi sayısı şu an büyük ihtimalle 0.</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Kanal</label>
                        <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value as any })} className={fieldClassName}>
                          <option value="EMAIL">E-posta</option><option value="SMS">SMS</option><option value="BOTH">E-posta + SMS</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={sectionClassName}>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <span className={sectionBadgeClassName}>2 · Kitle</span>
                    <div className="flex items-center gap-3">
                      {preview && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-sm">
                          <Users className="w-3 h-3" />{preview.count} kişi
                        </span>
                      )}
                      <button type="button" onClick={runPreview} disabled={previewing}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
                        <Users className="w-4 h-4" />{previewing ? "Hesaplanıyor…" : "Kitleyi Hesapla"}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Departmanlar <span className="text-gray-400 font-normal">(boş = tüm üyeler)</span></label>
                      <DepartmentSelector value="" onChange={(id, dep) => { if (id && !departments.some((d) => d.id === id)) setDepartments([...departments, { id, name: dep?.name || id }]); }} placeholder="Departman ekle…" />
                      {departments.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {departments.map((d) => (
                            <span key={d.id} className="inline-flex items-center gap-1 rounded-full bg-cyan-50 border border-cyan-100 px-2.5 py-1 text-xs font-medium text-cyan-800">
                              {d.name}<button type="button" onClick={() => setDepartments(departments.filter((x) => x.id !== d.id))} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">En az … gündür girmemiş</label>
                        <input type="number" min={0} value={aud.inactiveDays} onChange={(e) => setAud({ ...aud, inactiveDays: e.target.value })} className={fieldClassName} placeholder="Örn: 14" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Son … günde girmiş</label>
                        <input type="number" min={0} value={aud.activeWithinDays} onChange={(e) => setAud({ ...aud, activeWithinDays: e.target.value })} className={fieldClassName} placeholder="Örn: 7" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Lisansı … gün içinde bitecek</label>
                        <input type="number" min={0} value={aud.licenseExpiresInDays} onChange={(e) => setAud({ ...aud, licenseExpiresInDays: e.target.value })} className={fieldClassName} placeholder="Örn: 10" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                      <label className="inline-flex items-center cursor-pointer gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={aud.hasOpenGains} onChange={(e) => setAud({ ...aud, hasOpenGains: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500" />
                        Yalnız çözülmemiş eksiği (kazanımı) olanlar
                      </label>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Üst sınır (kişi)</label>
                        <input type="number" min={1} max={2000} value={aud.limit} onChange={(e) => setAud({ ...aud, limit: e.target.value })} className={fieldClassName} />
                      </div>
                    </div>
                    {preview && (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                        {excludedText(preview.excluded) || "Dışlanan yok."}
                        {preview.sample?.length ? <span className="text-slate-500"> · Örnek: {preview.sample.slice(0, 8).map((s: any) => s.ad).join(", ")}{preview.count > 8 ? "…" : ""}</span> : null}
                      </div>
                    )}
                  </div>
                </div>

                <div className={sectionClassName}>
                  <div className="mb-3"><span className={sectionBadgeClassName}>3 · {form.mode === "TEMPLATE" ? "Gönderilecek Metin" : "AI Yönergesi"}</span></div>
                  {form.mode === "TEMPLATE" ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Konu başlığı <span className="text-gray-400 font-normal">(boş bırakırsan AI metne uygun bir başlık yazar)</span></label>
                        <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={fieldClassName} placeholder="Örn: Sami Görey'den veda mektubu" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Metin *</label>
                        <textarea value={form.template} onChange={(e) => setForm({ ...form, template: e.target.value })} rows={14} className={fieldClassName}
                          placeholder={"Mektubu/duyuruyu olduğu gibi yapıştır.\n\nHitap satırını YAZMA — sistem her kişiye \"Sevgili Ahmet,\" gibi kendi hitabını ekler.\n\nİstersen metin içinde {ad} {soyad} {adSoyad} {departman} {sinif} yer tutucularını kullanabilirsin."} required />
                        <p className="mt-2 flex items-start gap-1.5 text-xs text-gray-500"><Sparkles className="w-3.5 h-3.5 mt-0.5 text-cyan-600 shrink-0" />Bu metne dokunulmaz — kısaltılmaz, yeniden yazılmaz. AI yalnız konu, ön başlık ve kişiye hitap satırını üretir.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Uyarlama notu <span className="text-gray-400 font-normal">(isteğe bağlı)</span></label>
                        <input value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} className={fieldClassName} placeholder="Örn: Öğrenciye hitap et, resmî ama sıcak bir ton kullan." />
                      </div>
                    </div>
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Amaç ve ton *</label>
                      <textarea value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} rows={5} className={fieldClassName}
                        placeholder="Örn: Bir süredir çalışmaya ara vermiş öğrencilere, açık kalan eksiklerini adıyla hatırlatan, bugün için 20 dakikalık somut bir başlangıç öneren sıcak bir mesaj. Suçlayıcı olma." />
                      <p className="mt-2 flex items-start gap-1.5 text-xs text-gray-500"><Sparkles className="w-3.5 h-3.5 mt-0.5 text-cyan-600 shrink-0" />Model her kişi için bu yönergeyi, o kişinin gerçek verisiyle (son giriş, açık eksikler, plan, lisans) birleştirir; en az iki somut noktaya değinmek zorundadır. Hizmet bildiriminde satış dili yasaktır.</p>
                    </>
                  )}
                </div>

                <div className="sticky bottom-0 z-10 -mx-2 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur">
                  <div className="flex items-center justify-end gap-3">
                    <button type="button" onClick={() => navigate("/mail-marketing")} className="px-4 py-2 bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 text-sm">İptal</button>
                    <button type="submit" disabled={!form.name.trim() || (form.mode === "TEMPLATE" ? !form.template.trim() : !form.brief.trim()) || isLoading}
                      className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-md hover:from-cyan-700 hover:to-blue-700 disabled:opacity-50 text-sm font-medium">
                      {isLoading ? "Kaydediliyor..." : "Kampanya Oluştur"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
